import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { style } from 'typestyle';
import DeleteIcon from 'mdi-react/DeleteOutlineIcon';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import * as styles from './offices.styles';
import * as tabStyles from './office-tabs.styles';
import * as formStyles from './office-form.styles';
import * as workspaceStyles from './office-workspace.styles';
import type Department from './models/department/department';
import OfficeInfoGroup from './groups/office-info-group.component';
import OfficeParkingGroup from './groups/office-parking-group.component';
import OfficeDepartmentsGroup from './groups/office-departments-group.component';
import OfficeRoomsGroup from './groups/office-rooms-group.component';
import OfficeAdminGroup from './groups/office-admin-group.component';
import OfficeEmailsGroup from './groups/office-emails-group.component';
import useRooms from './hooks/rooms.hook';
import type Room from './models/room/room';
import DeleteOfficeModal from './modals/delete-office-modal.component';
import useFacilitiesForOffice from './hooks/facilities.hook';
import ActivityIndicator from '../../core/components/activity-indicator/activity-indicator.component';
import { Center } from '../../core/components/center/center.component';
import Column from '../../core/components/column/column.component';
import { IsMobileContext } from '../../core/context/is-mobile';
import StyleConstants from '../../core/styles.constants';
import OfficeFacilitiesGroup from '../facilities/groups/office-facilities-group.component';
import CommonStyles from '../../core/common.styles';
import OfficeAnnouncementsGroup from './groups/office-announcements-group.component';
import useAnnouncements from './hooks/announcements.hook';
import type Announcement from './models/announcement';
import { useOfficeInformation } from './hooks/offices.hook';
import useOfficeNotifications from './hooks/office-notification.hook';
import OfficeNotification from './models/office-notification';
import OfficeNotificationsGroup from './groups/office-notification-group.component';
import OfficeCateringGroup from './groups/office-catering-group.component';

interface Props {
  readonly officeId?: string | undefined;
  /** `acknowledged`: the impact the delete dialog showed, if any, was accepted. */
  readonly onOfficeDelete?: (acknowledged: boolean) => Promise<void>;
}

type OfficeTabKey = 'general' | 'access' | 'rooms' | 'facilities' | 'parkings' | 'communication' | 'catering';

const OFFICE_TAB_KEYS: OfficeTabKey[] = ['general', 'access', 'rooms', 'facilities', 'parkings', 'communication', 'catering'];

const OfficeInfoOverview = ({ officeId, onOfficeDelete }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  const isCreatingOffice = officeId === undefined;
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { rooms } = useRooms({ id: officeId });
  const [roomsToShow, setRoomsToShow] = useState<Room[] | undefined>(undefined);

  const announcements = useAnnouncements({ id: officeId });
  const [announcementsToShow, setAnnouncementsToShow] = useState<Announcement[] | undefined>(undefined);
  const [announcementsError, setAnnouncementsError] = useState<number | undefined>(undefined);

  const officeNotifications = useOfficeNotifications({ id: officeId });
  const [officeNotificationsToShow, setOfficeNotificationsShow] = useState<OfficeNotification[] | undefined>(undefined);
  const [officeNotificationsErrors, setOfficeNotificationError] = useState<number | undefined>(undefined);

  // Reported by the office details form while it is mounted. Undefined until then, so the rooms
  // tab can still derive the same value straight from the office when the general tab was never
  // opened (tabs mount lazily).
  const [canEditRoomCapacityFromForm, setCanEditRoomCapacityFromForm] = useState<boolean | undefined>(undefined);

  // Owned by the departments card (it does the fetching and the CRUD); mirrored here so the rooms
  // card next to it can group and filter by department without a second request.
  const [departments, setDepartments] = useState<Department[] | undefined>(undefined);

  const facilityState = useFacilitiesForOffice({ officeId });
  const { facilities } = facilityState;

  const { office, refresh } = useOfficeInformation({ id: officeId });

  const canEditRoomCapacity = canEditRoomCapacityFromForm ?? (office != null ? !office.floorPlanEnabled : false);

  const setCanEditRoomCapacityOfParent = useCallback((canEdit: boolean): void => {
    setCanEditRoomCapacityFromForm(canEdit);
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo<OfficeTabKey>(() => {
    const requested = searchParams.get('tab');
    return OFFICE_TAB_KEYS.includes(requested as OfficeTabKey) ? (requested as OfficeTabKey) : 'general';
  }, [searchParams]);

  // Tabs lazy-mount on first visit and then stay mounted, hidden via display:none, so switching
  // back keeps in-progress edits and already-fetched data (same approach as the statistics page).
  const [visitedTabs, setVisitedTabs] = useState<Set<OfficeTabKey>>(() => new Set([activeTab]));
  useEffect(() => {
    setVisitedTabs((current) => (current.has(activeTab) ? current : new Set(current).add(activeTab)));
  }, [activeTab]);

  const setTab = useCallback(
    (tab: OfficeTabKey): void => {
      setSearchParams({ tab });
    },
    [setSearchParams],
  );

  // Adopts whatever the hooks last fetched, rather than only their first result.
  //
  // These `*ToShow` copies are what the cards below actually render — `rooms` is
  // handed to both the rooms and the departments group. The `== null` guards
  // this replaces took each copy once and never refreshed it, so a fetch for a
  // newly opened office arrived and was discarded, and the previous office's
  // rooms stayed on screen under the new office's name. Fixing the hooks alone
  // did not surface here for exactly that reason.
  //
  // The hooks only fetch when the office id changes, so this cannot clobber an
  // edit made in a card between navigations.
  useEffect(() => {
    if (rooms != null) {
      setRoomsToShow(rooms);
    }

    if (announcements != null) {
      if (announcements.error) {
        setAnnouncementsError(announcements.errorNumber);
      }
      setAnnouncementsToShow(announcements.announcements);
    }

    if (officeNotifications != null) {
      if (officeNotifications.error) {
        setOfficeNotificationError(officeNotifications.errorNumber);
      }
      setOfficeNotificationsShow(officeNotifications.officeNotifications);
    }
  }, [rooms, announcements, officeNotifications]);

  const isLoading = officeId !== undefined && roomsToShow == null && announcementsToShow == null && officeNotificationsToShow == null;

  // Each tab waits only on the data it actually renders — a failing announcements call must not
  // leave the rooms tab spinning on data that already arrived.
  // The office as well as its rooms: whether capacity follows a floorplan is read from it, and
  // before it arrives the rooms card would treat every office as a floorplan office.
  const roomsReady = roomsToShow != null && office != null;
  const communicationReady = announcementsToShow != null && officeNotificationsToShow != null;
  const officeReady = office != null;

  // Deleting still requires the office itself plus a delete handler from the parent.
  const canDeleteOffice = !isCreatingOffice && onOfficeDelete != null && officeReady;

  // Seats across this office's rooms, so the capacity percentage on the General tab can be shown
  // as real numbers. Undefined while the rooms are still loading (and when creating an office).
  const totalSeats = roomsToShow?.reduce((total, room) => total + room.capacity, 0);

  // Sits under Save in the form's summary rail; the handler and confirmation stay here.
  const deleteAction = canDeleteOffice ? (
    <button
      type="button"
      className={style(formStyles.railDeleteButton)}
      onClick={(): void => {
        setShowDeleteModal(true);
      }}
    >
      <span>{t('delete-office')}</span>
      <DeleteIcon size={18} />
    </button>
  ) : undefined;

  const officeDetailsForm = (
    <OfficeInfoGroup
      isCreatingOffice={isCreatingOffice}
      officeId={officeId}
      setCanEditRoomCapacity={setCanEditRoomCapacityOfParent}
      office={office}
      onSaved={refresh}
      totalSeats={totalSeats}
      deleteAction={deleteAction}
    />
  );

  const tabLoader = (
    <Center>
      <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
    </Center>
  );

  const panelClass = (tabKey: OfficeTabKey): string => style(activeTab === tabKey ? undefined : tabStyles.hiddenPanel);

  if (isCreatingOffice) {
    return (
      <div className={style(styles.bottomMargin)}>
        <Column className={style(CommonStyles.bodyMargin(isMobile))}>{officeDetailsForm}</Column>
      </div>
    );
  }

  return (
    <div className={style(styles.bottomMargin)}>
      <Column className={style(CommonStyles.bodyMargin(isMobile))}>
        {isLoading ? (
          tabLoader
        ) : (
          <>
            <h1 className={style(tabStyles.pageTitle)}>{office?.name ?? ''}</h1>
            <p className={style(tabStyles.pageSub)}>{t('office-detail-page-subtitle')}</p>

            <div className={style(tabStyles.tabBar(isMobile))} role="tablist">
              {OFFICE_TAB_KEYS.map((tabKey) => (
                <div
                  key={tabKey}
                  role="tab"
                  tabIndex={0}
                  aria-selected={activeTab === tabKey}
                  className={style(tabStyles.tab(activeTab === tabKey))}
                  onClick={(): void => setTab(tabKey)}
                  onKeyDown={(event): void => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      setTab(tabKey);
                    }
                  }}
                >
                  {t(`office-tab-${tabKey}`)}
                </div>
              ))}
            </div>

            {visitedTabs.has('general') && (
              <div className={panelClass('general')}>
                {officeDetailsForm}
                {canDeleteOffice && (
                  <DeleteOfficeModal
                    showModal={showDeleteModal}
                    officeId={officeId}
                    officeName={t(office?.name ?? 'Unknown Office')}
                    handleAccept={async (acknowledged): Promise<void> => {
                      await onOfficeDelete(acknowledged);
                      // Closed after a failure too, so opening it again re-reads what the deletion would cancel.
                      setShowDeleteModal(false);
                    }}
                    handleClose={(): void => {
                      setShowDeleteModal(false);
                    }}
                  />
                )}
              </div>
            )}

            {visitedTabs.has('access') && (
              <div className={panelClass('access')}>
                <div className={style(tabStyles.accessGrid(isMobile))}>
                  <OfficeEmailsGroup officeId={officeId} />
                  <OfficeAdminGroup officeId={officeId} />
                </div>
              </div>
            )}

            {visitedTabs.has('rooms') && (
              <div className={panelClass('rooms')}>
                {!roomsReady && tabLoader}
                {roomsReady && (
                  <div className={style(workspaceStyles.workspaceGrid(isMobile))}>
                    <OfficeDepartmentsGroup officeId={officeId} rooms={roomsToShow} onDepartmentsChange={setDepartments} />
                    <OfficeRoomsGroup
                      officeId={officeId}
                      canEditRoomCapacity={canEditRoomCapacity}
                      floorPlanEnabled={office?.floorPlanEnabled || false}
                      rooms={roomsToShow}
                      setRoomsDispatch={setRoomsToShow}
                      departments={departments}
                    />
                  </div>
                )}
              </div>
            )}

            {visitedTabs.has('facilities') && (
              <div className={panelClass('facilities')}>
                <OfficeFacilitiesGroup allFacilities={facilities} initialized={facilityState.initialized} readonly />
              </div>
            )}

            {visitedTabs.has('parkings') && (
              <div className={panelClass('parkings')}>
                <OfficeParkingGroup officeId={officeId} />
              </div>
            )}

            {visitedTabs.has('communication') && (
              <div className={panelClass('communication')}>
                {communicationReady ? (
                  <>
                    <OfficeAnnouncementsGroup
                      announcements={announcementsToShow}
                      announcementsError={announcementsError}
                      officeId={officeId}
                    />
                    <OfficeNotificationsGroup
                      officeNotifications={officeNotificationsToShow}
                      officeNotificationsError={officeNotificationsErrors}
                      officeId={officeId}
                    />
                  </>
                ) : (
                  tabLoader
                )}
              </div>
            )}

            {visitedTabs.has('catering') && (
              <div className={panelClass('catering')}>
                {officeReady ? <OfficeCateringGroup officeId={officeId} office={office} /> : tabLoader}
              </div>
            )}
          </>
        )}
      </Column>
    </div>
  );
};

export default OfficeInfoOverview;
